import { useEffect, useState } from 'react';
import axios from '../../axios.js';
import CircularProgress from '@mui/material/CircularProgress';
import { useLocation, useNavigate } from 'react-router-dom';

import Results from '../../components/Results/Results.js';
import Pager from '../../components/Pager/Pager.js';
import SearchBar from '../../components/SearchBar/SearchBar.js';

import './Search.css';

export default function SearchMagazine({ isFrench }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [results, setResults] = useState([]);
  const [resultCount, setResultCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [q, setQ] = useState(
    () => new URLSearchParams(location.search).get('q') ?? ''
  );

  const [top] = useState(
    () => Number(new URLSearchParams(location.search).get('top')) || 10
  );

  const [filters, setFilters] = useState([]);
  const [facets, setFacets] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [sortDate, setSortDate] = useState(false);

  // Zero means the user hasn't submitted a search yet.
  const [searchVersion, setSearchVersion] = useState(0);

  const skip = (currentPage - 1) * top;
  const searchPath = isFrench
    ? '/search-magazine-fr'
    : '/search-magazine';

  useEffect(() => {
    // Don't request anything on initial render.
    if (searchVersion === 0) return;

    // Ignore responses from an older search or an unmounted component.
    let ignore = false;

    setIsLoading(true);

    const body = {
      q,
      top,
      skip,
      filters: [{ field: 'source', value: 'CIM Magazine' }],
      sortDate,
      isFrench,
    };

    axios
      .post('/api/search', body)
      .then((response) => {
        if (ignore) return;

        setResults(response.data.result.results);
        setFacets(response.data.result.facets);
        setResultCount(response.data.result.count);
      })
      .catch((error) => {
        if (ignore) return;

        console.error(error);
        setResults([]);
        setFacets({});
        setResultCount(0);
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [searchVersion, q, top, skip, sortDate, isFrench]);

  const postSearchHandler = (searchTerm) => {
    const term = searchTerm.trim();

    // Don't submit empty searches.
    if (!term) return;

    setQ(term);
    setCurrentPage(1);
    setFilters([]);
    setIsLoading(true);

    // Incrementing also supports resubmitting the same term.
    setSearchVersion((version) => version + 1);

    navigate(`${searchPath}?q=${encodeURIComponent(term)}`);
  };

  let body = (
  <div
    className="text-center py-5"
    role="status"
    style={{ color: '#666' }}
  >
    <h2 className="h5">
      {isFrench ? 'Rechercher dans CIM Magazine' : 'Search CIM Magazine'}
    </h2>
    <p>
      {isFrench
        ? 'Saisissez un mot-clé, puis cliquez sur Rechercher ou appuyez sur Entrée.'
        : 'Type a keyword, then click Search or press Enter.'}
    </p>
  </div>
);

  if (isLoading) {
    body = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          marginTop: '5%',
        }}
      >
        <CircularProgress style={{ color: '#dd2a00' }} />
      </div>
    );
  } else if (searchVersion > 0) {
    body = (
      <div className="col-md-12">
        <Results
          documents={results}
          top={top}
          skip={skip}
          count={resultCount}
          query={q}
          sortDate={sortDate}
          setSortDate={setSortDate}
          color="#dd2a00"
          source="mag-eng"
        />

        <Pager
          className="pager-style"
          currentPage={currentPage}
          resultCount={resultCount}
          resultsPerPage={top}
          setCurrentPage={setCurrentPage}
          color="#14143c"
        />
      </div>
    );
  }

  return (
    <main className="container-fluid px-4 py-3">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex">
            <SearchBar
              postSearchHandler={postSearchHandler}
              query={q}
              facets={facets}
              filters={filters}
              setFilters={setFilters}
              color="#dd2a00"
              source="mag-eng"
              isFrench={isFrench}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">{body}</div>
      </div>
    </main>
  );
}